import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import ImageUploader from '../../components/ImageUploader';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Image as ImageIcon,
  GripVertical,
  Eye,
  EyeOff,
  ArrowUp,
  ArrowDown
} from 'lucide-react';

const AdminHomepagePhotos = () => {
  const { token } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    section: 'hero',
    order: 0,
    is_active: true
  });

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`${API}/admin/homepage-photos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPhotos(response.data);
    } catch (error) {
      console.error('Failed to fetch homepage photos:', error);
      toast.error('Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhotos();
  }, [token]);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      image_url: '',
      section: 'hero',
      order: photos.length,
      is_active: true
    });
    setEditing(null);
  };

  const handleSubmit = async () => {
    if (!formData.image_url) {
      toast.error('Please add an image');
      return;
    }

    try {
      if (editing) {
        await axios.put(`${API}/admin/homepage-photos/${editing}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Photo updated successfully!');
      } else {
        await axios.post(`${API}/admin/homepage-photos`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Photo added successfully!');
      }
      setShowDialog(false);
      resetForm();
      fetchPhotos();
    } catch (error) {
      console.error('Failed to save photo:', error);
      toast.error('Failed to save photo');
    }
  };

  const handleEdit = (photo) => {
    setFormData({
      title: photo.title || '',
      description: photo.description || '',
      image_url: photo.image_url,
      section: photo.section,
      order: photo.order,
      is_active: photo.is_active
    });
    setEditing(photo.id);
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    
    try {
      await axios.delete(`${API}/admin/homepage-photos/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Photo deleted!');
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const handleToggleActive = async (photo) => {
    try {
      await axios.patch(`${API}/admin/homepage-photos/${photo.id}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Photo ${photo.is_active ? 'hidden' : 'shown'}!`);
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to update photo');
    }
  };

  const handleReorder = async (photoId, direction) => {
    try {
      await axios.patch(`${API}/admin/homepage-photos/${photoId}/reorder`, 
        { direction },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to reorder');
    }
  };

  const getSectionLabel = (section) => {
    switch (section) {
      case 'hero': return 'Hero Section';
      case 'gallery': return 'Gallery Row';
      case 'community': return 'Community Section';
      default: return section;
    }
  };

  const groupedPhotos = photos.reduce((acc, photo) => {
    const section = photo.section || 'hero';
    if (!acc[section]) acc[section] = [];
    acc[section].push(photo);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-plp-blue"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-homepage-photos">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Homepage Photos</h1>
          <p className="text-slate-600">Manage photos displayed on the homepage</p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-plp-blue hover:bg-plp-blue-light" data-testid="add-photo-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Photo
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Photo' : 'Add New Photo'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <ImageUploader
                value={formData.image_url}
                onChange={(url) => setFormData({...formData, image_url: url})}
                label="Photo *"
              />

              <div className="space-y-2">
                <Label htmlFor="title">Title (optional)</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="e.g., Community Event 2025"
                  data-testid="photo-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description (optional)</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Brief description of the photo"
                  rows={2}
                  data-testid="photo-desc-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Section</Label>
                <select
                  value={formData.section}
                  onChange={(e) => setFormData({...formData, section: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  data-testid="photo-section-select"
                >
                  <option value="hero">Hero Section (Main Image)</option>
                  <option value="gallery">Gallery Row</option>
                  <option value="community">Community Section</option>
                </select>
              </div>

              <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg">
                <div>
                  <Label>Visible</Label>
                  <p className="text-sm text-slate-500">Show on homepage</p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                  data-testid="photo-active-switch"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSubmit}
                  className="bg-plp-blue hover:bg-plp-blue-light"
                  data-testid="photo-submit-btn"
                >
                  {editing ? 'Update Photo' : 'Add Photo'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-plp-blue/10 rounded-xl flex items-center justify-center">
                <ImageIcon className="w-6 h-6 text-plp-blue" />
              </div>
              <div>
                <p className="text-2xl font-bold">{photos.length}</p>
                <p className="text-sm text-slate-500">Total Photos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center">
                <Eye className="w-6 h-6 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{photos.filter(p => p.is_active).length}</p>
                <p className="text-sm text-slate-500">Visible Photos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center">
                <GripVertical className="w-6 h-6 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{Object.keys(groupedPhotos).length}</p>
                <p className="text-sm text-slate-500">Sections</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Photos by Section */}
      {photos.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No homepage photos yet. Add your first photo!</p>
          </CardContent>
        </Card>
      ) : (
        ['hero', 'gallery', 'community'].map(section => (
          groupedPhotos[section] && groupedPhotos[section].length > 0 && (
            <Card key={section}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {getSectionLabel(section)}
                  <Badge variant="outline">{groupedPhotos[section].length} photos</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedPhotos[section]
                    .sort((a, b) => a.order - b.order)
                    .map((photo, index) => (
                    <div 
                      key={photo.id}
                      className={`border rounded-lg overflow-hidden ${!photo.is_active && 'opacity-50'}`}
                      data-testid={`homepage-photo-${photo.id}`}
                    >
                      <div className="aspect-video relative">
                        <img 
                          src={photo.image_url.startsWith('/api') 
                            ? `${API.replace('/api', '')}${photo.image_url}` 
                            : photo.image_url
                          }
                          alt={photo.title || 'Homepage photo'}
                          className="w-full h-full object-cover"
                        />
                        {!photo.is_active && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <Badge className="bg-slate-700">Hidden</Badge>
                          </div>
                        )}
                        <div className="absolute top-2 right-2 flex gap-1">
                          <button
                            onClick={() => handleReorder(photo.id, 'up')}
                            disabled={index === 0}
                            className="p-1 bg-white/80 rounded hover:bg-white disabled:opacity-50"
                          >
                            <ArrowUp className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleReorder(photo.id, 'down')}
                            disabled={index === groupedPhotos[section].length - 1}
                            className="p-1 bg-white/80 rounded hover:bg-white disabled:opacity-50"
                          >
                            <ArrowDown className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="p-3">
                        {photo.title && (
                          <h3 className="font-medium text-sm mb-1">{photo.title}</h3>
                        )}
                        {photo.description && (
                          <p className="text-xs text-slate-500 line-clamp-2">{photo.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleActive(photo)}
                            className="flex-1"
                          >
                            {photo.is_active ? (
                              <><EyeOff className="w-3 h-3 mr-1" /> Hide</>
                            ) : (
                              <><Eye className="w-3 h-3 mr-1" /> Show</>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(photo)}
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(photo.id)}
                            className="text-red-500"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )
        ))
      )}
    </div>
  );
};

export default AdminHomepagePhotos;
